import React, { useEffect, useRef } from "react";
import type { FaceStatus } from "../../../../../../hooks/useMediaPipe";

interface CameraViewProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  hidden: boolean;
  hasCamera: boolean;
  waitingForPermission: boolean;
  modelReady: boolean;
  faceStatus: FaceStatus;
}

/** Live camera preview with the face landmark overlay and detection badge */
const CameraView: React.FC<CameraViewProps> = ({
  videoRef,
  hidden,
  hasCamera,
  waitingForPermission,
  modelReady,
  faceStatus,
}) => {
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);

  // ── Mirror landmark canvas onto overlay ───────────────────────────────────
  useEffect(() => {
    let rafId = 0;
    const paint = () => {
      const src = faceStatus.landmarkCanvas;
      const dst = overlayCanvasRef.current;
      if (src && dst && src.width > 0) {
        dst.width = src.width;
        dst.height = src.height;
        const ctx = dst.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, dst.width, dst.height);
          ctx.drawImage(src, 0, 0);
        }
      }
      rafId = requestAnimationFrame(paint);
    };
    rafId = requestAnimationFrame(paint);
    return () => cancelAnimationFrame(rafId);
  }, [faceStatus.landmarkCanvas]);

  const faceIndicatorColor = !modelReady
    ? "#9ca3af"
    : faceStatus.count === 0
      ? "#ef4444"
      : faceStatus.count > 1
        ? "#f59e0b"
        : "#22c55e";

  const faceIndicatorText = !modelReady
    ? "Loading model…"
    : faceStatus.count === 0
      ? "No face detected"
      : faceStatus.count > 1
        ? `${faceStatus.count} faces — only 1 allowed`
        : "Face detected";

  return (
    <div
      className={`relative rounded-xl overflow-hidden bg-black w-[250px] h-[250px] max-w-full mx-auto ${hidden ? "hidden" : ""}`}
    >
      <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
      <canvas ref={overlayCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      {hasCamera ? (
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium">
          <span
            className="w-2.5 h-2.5 rounded-full animate-pulse"
            style={{ backgroundColor: faceIndicatorColor }}
          />
          {faceIndicatorText}
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm px-6 text-center">
          {waitingForPermission ? "Waiting for camera permission…" : "Camera preview unavailable"}
        </div>
      )}
    </div>
  );
};

export default CameraView;
